interface TrendPoint {
  date: string;
  value: number;
}

interface ProductTrendSparklineProps {
  points?: TrendPoint[];
  change7d?: number | null;
  change30d?: number | null;
  change90d?: number | null;
  compact?: boolean;
}

const WIDTH = 300;
const HEIGHT = 58;
const PAD = 3;

function trendPath(points: TrendPoint[]): string {
  if (points.length < 2) return "";
  let min = Infinity;
  let max = -Infinity;
  for (const point of points) {
    min = Math.min(min, point.value);
    max = Math.max(max, point.value);
  }
  const span = Math.max(max - min, max * 0.025, 1);
  return points.map((point, index) => {
    const x = PAD + (index / (points.length - 1)) * (WIDTH - PAD * 2);
    const y = HEIGHT - PAD - ((point.value - min) / span) * (HEIGHT - PAD * 2);
    return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
}

function Change({ label, value }: { label: string; value?: number | null }) {
  const available = typeof value === "number" && Number.isFinite(value);
  const tone = !available ? "text-(--color-muted)" : value > 0 ? "text-(--trend-up)" : value < 0 ? "text-(--trend-down)" : "text-(--color-muted)";
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-(--color-muted)">{label}</span>
      <strong className={`font-(family-name:--font-mono) text-[10px] ${tone}`}>
        {available ? `${value > 0 ? "+" : ""}${value.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}%` : "—"}
      </strong>
    </span>
  );
}

export default function ProductTrendSparkline({ points = [], change7d, change30d, change90d, compact = false }: ProductTrendSparklineProps) {
  const clean = points.filter((point) => Number.isFinite(point.value));
  const path = trendPath(clean);
  const first = clean[0]?.value;
  const last = clean.at(-1)?.value;
  const stroke = typeof first !== "number" || typeof last !== "number" || last === first
    ? "var(--color-muted)"
    : last > first ? "var(--trend-up)" : "var(--trend-down)";

  return (
    <div className={compact ? "mt-3 border-t border-(--color-border-soft) pt-3" : "min-w-0"}>
      <div className={compact ? "mb-1.5 grid gap-1" : "mb-1.5 flex items-center justify-between gap-3"}>
        <span className="font-(family-name:--font-mono) text-[9px] font-bold uppercase tracking-[0.12em] text-(--color-muted)">90 günlük seyir</span>
        <div className={compact ? "flex items-center justify-between gap-2" : "flex gap-2.5"}>
          <Change label="7G" value={change7d} />
          <Change label="30G" value={change30d} />
          <Change label="90G" value={change90d} />
        </div>
      </div>
      {path ? (
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" className={`${compact ? "h-12" : "h-14"} w-full overflow-visible`} role="img" aria-label="Ürünün son 90 günlük orta fiyat seyri">
          <path d={`M${PAD},${HEIGHT - PAD} H${WIDTH - PAD}`} stroke="var(--border)" strokeWidth="1" strokeDasharray="3 4" fill="none" />
          <path d={`${path} L${WIDTH - PAD},${HEIGHT - PAD} L${PAD},${HEIGHT - PAD} Z`} fill={stroke} opacity="0.07" />
          <path d={path} stroke={stroke} strokeWidth="2.4" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <circle cx={WIDTH - PAD} cy={path.match(/L([\d.]+),([\d.]+)$/)?.[2] ?? HEIGHT / 2} r="3" fill={stroke} />
        </svg>
      ) : (
        <div className={`${compact ? "h-12" : "h-14"} flex items-center border-y border-dashed border-(--color-border-soft) text-[10px] text-(--color-muted)`}>Dönem grafiği için yeterli geçmiş yok</div>
      )}
    </div>
  );
}
